import { Injectable } from '@angular/core';
import { ListItem, ReferenceData, Asset, Channel, Country, ICountryByName, AemCountry, SubClassification, AppTimeZoneInfo, GlossaryListItem } from './models';
import { execute, getWindowsTimeZoneDescriptionWithoutOffset, getWindowsTimeZoneDescriptionWithoutOffsetById } from './utils';
import { IListInfo, ITimeZoneInfo, PermissionKind, sp } from '@pnp/sp/presets/all';
import { ListTitles } from './constants';
import { CurrentUserService } from './current-user.service';
import { findWindows } from 'windows-iana';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';

export interface IChannelNavigationUrlByAcromym {
  [acronym: string]: string;
}

export interface ChannelByAcronym {
  [acronym: string]: Channel;
}

export interface ChannelByUrl {
  [url: string]: Channel;
}

interface ListInfoPromiseDictionary {
  [listTitle: string]: Promise<IListInfo>;
}

export const channelNavigationUrlByAcromym: IChannelNavigationUrlByAcromym = {
  AEM: '/Lists/AEM%20Metrics/AEM.aspx?t=aem',
  AEMG: '/Lists/AEM%20Metrics/AEM.aspx?t=aem',
  ESA: '/Lists/HTA%20Early%20Scientific%20Advice/AllItems.aspx?t=esaadvice',
  ESAG: '/Lists/HTA%20Early%20Scientific%20Advice/AllItems.aspx?t=esaadvice',
  HSR: '/Lists/HTASubmission%20Resources/AllItems.aspx?t=hsrresources',
  HSRG: '/Lists/HTASubmission%20Resources/AllItems.aspx?t=hsrresources',
  HST: '/Lists/SubmissionTimeline/AllItems.aspx',
  EEP: '/SitePages/EEPGuidance.aspx',
  EEPR: '/SitePages/EEPGuidance.aspx',
  //AERC: '/SitePages/AccessCatalog.aspx',
  PHD: '/SitePages/PastHTADecisions.aspx',
  HMC: '/Lists/HTA Economic Model Cocreation/AllItems.aspx?t=htaemcr',
  MAAP: '/Lists/Market%20Access%20Analysis%20Plan/AllItems.aspx?t=maapg',
  EduHTA: '/SitePages/HTAEducation.aspx?t=htaedu',
  HTAT: '/SitePages/HTA-Trends.aspx',
  TP: '/Lists/TP1TP2/AllItems.aspx?t=tp',
  AC: '/SitePages/AccessCommunities.aspx',
 
  /* AccessHUb-3.15-Relese  */
  FAQ: '/Lists/FAQQuestions/AllItems.aspx',
  MADB: '/Lists/MarketAccessDatabases/AllItems.aspx',
  CR: '/Lists/ContractRightBestPracticeExamples/AllItems.aspx',
  AERC: '/Lists/AERCCountryComparisonData/AllItems.aspx',
  HMCNMA: '/Lists/HTA Economic Model Cocreation/AllItems.aspx?t=htaemcr',
  ACHTAFAQ: '/Lists/ACHTAFAQQuestions/AllItems.aspx',
  MAET: '/Lists/Market%20Access%20Analysis%20Plan/AllItems.aspx?t=maapg',
  /* AccessHUb-3.15-Relese  */
}


@Injectable({
  providedIn: 'root'
})
export class ReferenceDataService {

  private referenceDataPromise: Promise<ReferenceData>;

  constructor(private currentUserService: CurrentUserService, config: NgbTooltipConfig) {
    config.placement = 'top';
    config.triggers = 'hover';
    config.tooltipClass = "tootltip";
   }

  async get(): Promise<ReferenceData> {
    if (!this.referenceDataPromise) {
      const assetsPromise = execute<ListItem[]>(() => sp.web.lists.getByTitle(ListTitles.AssetsMaster).items.expand('TA').select('*,TA/Title,TA/ID').getAll());
      const indicationsPromise = execute<ListItem[]>(() => sp.web.lists.getByTitle(ListTitles.IndicationMaster).items.expand('TA').select('*,TA/Title,TA/ID').getAll());
      const tasPromise = execute<ListItem[]>(() => sp.web.lists.getByTitle(ListTitles.TAMaster).items.orderBy("SortOrder", true).getAll());

      const assets = await assetsPromise;
      const indications = await indicationsPromise;
      const tas = await tasPromise;

      const channelsPromise = execute<Channel[]>(() => sp.web.lists.getByTitle(ListTitles.Channels).items.getAll());
      const collateralTypePromise = execute<ListItem[]>(() => sp.web.lists.getByTitle(ListTitles.CollateralTypes).items.getAll());
      const aemCountriesPromise = execute<AemCountry[]>(() => sp.web.lists.getByTitle(ListTitles.CountryAem).items.expand('Channels').select('*,Channels/Title').getAll());

      const channels = await channelsPromise;
      const collateralTypes = await collateralTypePromise;
      const aemCountries = await aemCountriesPromise;

      const assetsById: Asset[] = [];
      assets.forEach(x => assetsById[x.Id] = x);
      assets.sort((a, b) => a.Title.localeCompare(b.Title));

      const typeOfInteractions = await execute<ListItem[]>(() => sp.web.lists.getByTitle(ListTitles.TypeOfInteractions).items.getAll());
      const typeOfInteractionsById: ListItem[] = [];
      typeOfInteractions.forEach(x => typeOfInteractionsById[x.Id] = x);
      typeOfInteractions.sort((a, b) => a.Title.localeCompare(b.Title));
      assets.sort((a, b) => a.Title.localeCompare(b.Title));

      const bussinessNeed = await execute<ListItem[]>(() => sp.web.lists.getByTitle(ListTitles.BusinessNeed).items.getAll());
      const bussinessNeedById: ListItem[] = [];
      bussinessNeed.forEach(x => bussinessNeedById[x.Id] = x);
      bussinessNeed.sort((a, b) => a.Title.localeCompare(b.Title));


      const indicationsByTitle: ListItem[] = [];
      // Eliminamos los paises sin canales, parece que esos tenemos que descartarlos.
      //aemCountries = aemCountries.filter(x => x.Channels.length != 0);
      aemCountries.forEach(x => indicationsByTitle[x.Title] = x.Title);
      aemCountries.sort((a, b) => a.Title.localeCompare(b.Title));


      const indicationsById: ListItem[] = [];
      indications.forEach(x => indicationsById[x.Id] = x);
      indications.sort((a, b) => a.Title.localeCompare(b.Title));


      const tasById: ListItem[] = [];
      tas.forEach(x => tasById[x.Id] = x);
      tas.sort((a, b) => a.Title.localeCompare(b.Title));


      const channelById: Channel[] = [];
      channels.forEach(x => channelById[x.Id] = x);
      channels.sort((a, b) => a.Id - b.Id);


      const collateralTypesById: ListItem[] = [];
      collateralTypes.forEach(x => collateralTypesById[x.Id] = x);
      collateralTypes.sort((a, b) => a.Id - b.Id);

      const referenceData: ReferenceData = {
        Assets: assets,
        AssetsById: assetsById,
        Indications: indications,
        IndicationsById: indicationsById,
        TAs: tas,
        TAsById: tasById,
        Channels: channels,
        ChannelsById: channelById,
        CollateralTypes: collateralTypes,
        CollateralTypesById: collateralTypesById,
        AemCountries: aemCountries,
        TypeOfInteractions: typeOfInteractions,
        TypeOfInteractionsById: typeOfInteractionsById,
        BussinessNeed: bussinessNeed,
        BussinessNeedById: bussinessNeedById
      };

      this.referenceDataPromise = Promise.resolve(referenceData);
    }
    return this.referenceDataPromise;
  }

  #countriesPromise: Promise<Country[]>;
  getCountries(): Promise<Country[]> {
    if (!this.#countriesPromise) {
      this.#countriesPromise = execute(() => sp.web.lists.getByTitle(ListTitles.CountryMaster).items.getAll());
      this.#countriesPromise.catch(error => {
        console.error('Failed to get countries from AllCountriesMaster: %o', error);
        this.#countriesPromise = undefined;
      });
    }
    return this.#countriesPromise;
  }

  #countryByNamePromise: Promise<ICountryByName>;
  getCountryByName(): Promise<ICountryByName> {
    if (!this.#countryByNamePromise) {
      this.#countryByNamePromise = this.getCountries()
        .then(countries => {
          const countryByName: ICountryByName = {};
          countries.forEach(c => {
            countryByName[c.Title] = c;
          });
          return countryByName;
        });
      this.#countryByNamePromise.catch(error => {
        console.error('Failed to get countries by name: %o', error);
      })
    }
    return this.#countryByNamePromise;
  }

  #htaKeywordsPromise: Promise<ListItem[]>;
  getHtaKeywords(): Promise<ListItem[]> {
    if (!this.#htaKeywordsPromise) {
      this.#htaKeywordsPromise = execute<ListItem[]>(() => sp.web.lists.getByTitle(ListTitles.HtaKeywords).items.filter(`isVerifiedKeyWord eq 1`).getAll());
      this.#htaKeywordsPromise.catch(error => {
        console.error('Failed to get keywords from HTA Keywords: %o', error);
        this.#htaKeywordsPromise = undefined;
      })
    }
    return this.#htaKeywordsPromise;
  }

  #htaHighlightTermsPromise: Promise<GlossaryListItem[]>;
  getHighlightHtaTerms(): Promise<GlossaryListItem[]> {
    if (!this.#htaHighlightTermsPromise) {
      this.#htaHighlightTermsPromise = execute<GlossaryListItem[]>(() => sp.web.lists.getByTitle(ListTitles.GlossaryTerms).items.getAll());
      this.#htaHighlightTermsPromise.catch(error => {
        console.error('Failed to get from HTA Terms: %o', error);
        this.#htaHighlightTermsPromise = undefined;
      })
    }
    return this.#htaHighlightTermsPromise;
  }

  async getHighlightHtaText(content: string): Promise<string> {

    let replacedString = content;
    const htaHighlightTextList: GlossaryListItem[] = await this.getHighlightHtaTerms();
    const replaceWords = {};
    const replaceWordKey = [];
    await htaHighlightTextList.forEach(async element => {
      if (element.TermDefinition && (element.HighlightTerm || element.HighlightTerm == null)) {
        let highlightTerm = [element.Title];
        if (element.Synonyms) {
          highlightTerm.push(...element.Synonyms.split('; '));
        }
        if (element.Abbreviations) {
          highlightTerm.push(...element.Abbreviations.split('; '));
        }
        await [...new Set(highlightTerm)].forEach((highlight) => {
          if (replacedString.includes(highlight) || replacedString.includes(highlight.toLowerCase())) {
             const rs = new RegExp('\\W' + highlight + '\\W','i');
            
            const matchStr = replacedString.match(rs);
            if (matchStr) {
              const match = matchStr.index;
              const lastIndex = match + highlight.length + 1;
              const firstchar = replacedString.substring(match, match + 1);
              const lastChar = replacedString.substring(lastIndex, lastIndex + 1);
              const lastCharbefore = replacedString.substring(lastIndex-1, lastIndex);
              const fmatch = firstchar.match(/[a-z]/i);
              const lmatch = lastChar.match(/[a-z]/i);
              const lbmatch = lastCharbefore.match(/[a-z]/i);
              const finalString = replacedString.substring(match+1,lastIndex); 
              if (fmatch && fmatch.length > 0 || lmatch && lmatch.length > 0 && lbmatch && lbmatch.length > 0 || lastChar == '>') {
                console.log(firstchar, lastChar);
              }  else {
                const r_key = element.Id.toString() + 'text';
                replaceWordKey.push(r_key);
               // replaceWords[r_key] = element.TermDefinition;
                var temp = document.createElement('div');
                 temp.innerHTML = element.TermDefinition;
                replaceWords[r_key] = temp.textContent;
                // if (!replacedString.includes(`>${highlight}</u>`)){
                  if(lmatch && lmatch.length ){
                    replacedString = replacedString.replace(rs, `<u  tooltip=${r_key} style="color: #1187c9 !important;" id='${element.Id}' class='tooltiptext'>${finalString}</u>`); 
                  //  replacedString = replacedString.replace(rs, `<u  tooltip=${r_key} style="color: #1187c9 !important;" id='${element.Id}' class='tooltiptext'>${highlight}</u>`);
                  }
                  else replacedString = replacedString.replace(rs, `${firstchar}<u tooltip=${r_key} style="color: #1187c9 !important;" id='${element.Id}' class='tooltiptext'>${finalString}</u>${lastChar}`); 
                //  else replacedString = replacedString.replace(rs, `${firstchar}<u tooltip=${r_key} style="color: #1187c9 !important;" id='${element.Id}' class='tooltiptext'>${highlight}</u>${lastChar}`);
                }
                  //  }
            }
            else if(this.containsSpecialCharacters(highlight)){
              const ss = (highlight);
              if(replacedString.includes(ss)){
                const r_key = element.Id.toString() + 'text';
                replaceWordKey.push(r_key);
               // replaceWords[r_key] = element.TermDefinition;
                var temp = document.createElement('div');
                temp.innerHTML = element.TermDefinition;
                replaceWords[r_key] = temp.textContent;
                if (!replacedString.includes(`>${ss}</u>`)){
                replacedString = replacedString.replace(ss, `<u tooltip=${r_key} style="color: #1187c9 !important;" id='${element.Id}' class='tooltiptext'>${ss}</u>`);
             
                }
              }else {
                const ssl = (highlight.toLowerCase());
                if(replacedString.includes(ssl)){
                  const r_key = element.Id.toString() + 'text';
                  replaceWordKey.push(r_key);
                //  replaceWords[r_key] = element.TermDefinition;
                  var temp = document.createElement('div');
                  temp.innerHTML = element.TermDefinition;
                  replaceWords[r_key] = temp.textContent;
                  if (!replacedString.includes(`>${ssl}</u>`)){
                  replacedString = replacedString.replace(ssl, `<u tooltip=${r_key} style="color: #1187c9 !important;" id='${element.Id}' class='tooltiptext'>${ssl}</u>`);  
                }
               
                }
              }
               
                 
            }
          } else if(this.containsSpecialCharacters(highlight)){
            const ss = new RegExp('\\B' + highlight + '\\B','i');
            
            
              const r_key = element.Id.toString() + 'text';
              replaceWordKey.push(r_key);
              replaceWords[r_key] = element.TermDefinition;
              if (!replacedString.includes(`>${highlight}</u>`)){
              replacedString = replacedString.replace(ss, `<u tooltip=${r_key} style="color: #1187c9 !important;" id='${element.Id}' class='tooltiptext'>${highlight}</u>`);
           
              }
            
             
               
          }
        });
      }
    });
    await replaceWordKey.forEach((highlightKey) => {
      const re = new RegExp(`=${highlightKey}`, 'gi');
      replacedString = replacedString.replace(re, `='${replaceWords[highlightKey]}'`);
    });
    return replacedString;
  }
  containsSpecialCharacters(str){
    var regex = /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g;
	return regex.test(str);
}
  #channelByAcronimPromise: Promise<ChannelByAcronym>;
  getChannelByAcronym(): Promise<ChannelByAcronym> {
    if (!this.#channelByAcronimPromise) {
      this.#channelByAcronimPromise = this.get().then(rd => {
        const channelByAcronim: ChannelByAcronym = {};
        const channelByUrl: ChannelByUrl = {};
        rd.Channels.forEach(ch => {
          channelByUrl[ch.NavigationURL] = ch;
        });
        for (const acronim in channelNavigationUrlByAcromym) {
          const navigationUrl = channelNavigationUrlByAcromym[acronim];
          const channel = channelByUrl[navigationUrl];
          if (channel) channelByAcronim[acronim] = channel;
        }
        return channelByAcronim;
      });
      this.#channelByAcronimPromise.catch(error => {
        console.error('Falied to get channel by acronim: %o', error);
        this.#channelByAcronimPromise = undefined;
      });
    }
    return this.#channelByAcronimPromise;
  }

  #subClassificationPromise: Promise<SubClassification[]>;
  getsubClassification(refresh = false): Promise<SubClassification[]> {
    if (!this.#subClassificationPromise || refresh === true) {
      this.#subClassificationPromise = execute<SubClassification[]>(() => sp.web.lists.getByTitle(ListTitles.SubClassification).items.getAll());
      this.#subClassificationPromise.catch(error => {
        console.error('Failed to get countries from AllCountriesMaster: %o', error);
        this.#subClassificationPromise = undefined;
      });
    }
    return this.#subClassificationPromise;
  }

  private listInfoPromiseDictionary: ListInfoPromiseDictionary = {};

  getListInfo(listTitle: string): Promise<IListInfo> {
    let promise = this.listInfoPromiseDictionary[listTitle];
    if (!promise) {
      promise = execute(() => sp.web.lists.getByTitle(listTitle).get());
      promise.catch(error => {
        console.error(`Failed to get information for list "${listTitle}": %o`, error);
        this.listInfoPromiseDictionary[listTitle] = undefined;
      });
      this.listInfoPromiseDictionary[listTitle] = promise;
    }
    return promise;
  }

  private timeZonesPromise: Promise<ITimeZoneInfo[]>

  getTimeZones(): Promise<ITimeZoneInfo[]> {
    if (!this.timeZonesPromise) {
      this.timeZonesPromise = execute(() => sp.web.regionalSettings.timeZones.get());
      this.timeZonesPromise.catch((err) => {
        console.error(err);
        this.timeZonesPromise = undefined;
      });
    }
    return this.timeZonesPromise;
  }



  private currentTimeZonePromise: Promise<AppTimeZoneInfo>

  private async getCurrentTimeZoneImplementation(): Promise<AppTimeZoneInfo> {
    const windowsTimeZones = await this.getTimeZones();
    const ianaIimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const windowsTimeZoneId = findWindows(ianaIimeZone)[0];
    const windowsTimeZoneDescriptionWithoutOffset = getWindowsTimeZoneDescriptionWithoutOffsetById(windowsTimeZoneId);
    for (const windowsTimeZone of windowsTimeZones) {
      const withoutOffset = getWindowsTimeZoneDescriptionWithoutOffset(windowsTimeZone.Description);
      if (withoutOffset && windowsTimeZoneDescriptionWithoutOffset) {
        if (withoutOffset.toLowerCase() === windowsTimeZoneDescriptionWithoutOffset.toLowerCase()) {
          return {
            iana: ianaIimeZone,
            windowsDescription: windowsTimeZone.Description,
            windowsWithoutOffset: windowsTimeZoneDescriptionWithoutOffset,
            windowsId: windowsTimeZoneId,
            displayName: windowsTimeZone.Description
          } as AppTimeZoneInfo;
        }
      }
    }
    return {
      iana: ianaIimeZone,
      windowsWithoutOffset: windowsTimeZoneDescriptionWithoutOffset,
      windowsId: windowsTimeZoneId,
      displayName: windowsTimeZoneDescriptionWithoutOffset || windowsTimeZoneId || ianaIimeZone
    } as AppTimeZoneInfo;
  }

  getCurrentTimeZone(): Promise<AppTimeZoneInfo> {
    if (!this.currentTimeZonePromise) {
      this.currentTimeZonePromise = this.getCurrentTimeZoneImplementation();
      this.currentTimeZonePromise.catch(() => {
        this.currentTimeZonePromise = undefined;
      })
    }
    return this.currentTimeZonePromise;
  }

  canAddPromises: any = {}

  canAdd(listTitle: string): Promise<boolean> {
    if (!this.canAddPromises[listTitle]) {
      this.canAddPromises[listTitle] = execute(() => sp.web.lists.getByTitle(listTitle).currentUserHasPermissions(PermissionKind.AddListItems));
      this.canAddPromises[listTitle].catch(() => {
        this.canAddPromises[listTitle] = undefined;
      });
    }
    return this.canAddPromises[listTitle];
  }
}
